import { useUser } from "@/client/services/user/user";

import { useUserAccess } from "./use-user-access";

export type TemplateAccess = {
  canEdit: boolean;
  canDownloadPdf: boolean;
  canShare: boolean;
  showWatermark: boolean;
  isPremiumTemplate: boolean;
};

export const useTemplateAccess = (templateTheme?: { isFree?: string }): TemplateAccess => {
  const { user } = useUser();
  const { userType, hasPremiumFeatures } = useUserAccess();
  const monthStartUtc = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));

  // Determine if template is free or premium
  // Only templates with isFree: "YES" are free - everything else (including missing field) is premium
  const isTemplateFreeTier = templateTheme?.isFree === "YES";
  const isPremiumTemplate = !isTemplateFreeTier;

  // Access rules based on user type and template type
  const canEdit = true; // All users can edit all templates
  let canDownloadPdf = false;
  let canShare = false;
  let showWatermark = false;

  if (userType === "GUEST" || userType === "FREE") {
    // Free and Guest Users
    if (userType === "FREE") {
      const lastFreePdfExportAt = user?.freePdfLastExportAt ? new Date(user.freePdfLastExportAt) : null;
      const hasMonthlyFreePdfRemaining = !lastFreePdfExportAt || lastFreePdfExportAt < monthStartUtc;
      canDownloadPdf = isTemplateFreeTier && hasMonthlyFreePdfRemaining;
    } else {
      canDownloadPdf = isTemplateFreeTier; // Guests can only download free templates
    }
    canShare = false; // Cannot enable sharing
    showWatermark = true; // Always show watermark in preview
  } else if (hasPremiumFeatures) {
    // Users with premium features (BASIC, PROFESSIONAL, ENTERPRISE)
    canDownloadPdf = true; // Can download all templates
    canShare = true; // Can share
    showWatermark = false; // No watermark for paid users
  } else if (userType === "TOKEN_USER") {
    // Token users (purchased tokens but no subscription)
    canDownloadPdf = isTemplateFreeTier; // Can only download free templates
    canShare = false; // Cannot enable sharing
    showWatermark = true; // Show watermark (no premium features)
  }

  return {
    canEdit,
    canDownloadPdf,
    canShare,
    showWatermark,
    isPremiumTemplate,
  };
};
